import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Smartphone, Trash2, LogOut, Pencil, Check, X } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { fmtDateTime } from "../lib/helpers";

export function DeviceList({ uid }) {
  const { t, lang } = useLang();
  const { user, setUser } = useAuth();
  const self = !uid || uid === user.id;
  const base = self ? "/me" : `/users/${uid}`;
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const saveLabel = async () => {
    try { await api.put(`${base}/devices/${edit.id}`, { label: edit.label }); toast.success(t("Device renamed", "Dispositivo renombrado")); setEdit(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const load = () => api.get(`${base}/devices`).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [base]); // eslint-disable-line react-hooks/exhaustive-deps
  const remove = async (d) => {
    if (!window.confirm(t(`Remove "${d.label || d.name}"? It will be signed out and treated as a new device next time.`, `¿Quitar "${d.label || d.name}"? Se cerrará su sesión y será un dispositivo nuevo la próxima vez.`))) return;
    try {
      const { data } = await api.delete(`${base}/devices/${d.id}`);
      toast.success(t("Device removed", "Dispositivo eliminado"));
      if (data.signed_out_current) return setUser(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const everywhere = async () => {
    if (!window.confirm(t("Sign out of ALL devices, including this one?", "¿Cerrar sesión en TODOS los dispositivos, incluido este?"))) return;
    try {
      await api.post(self ? "/auth/logout-everywhere" : `/users/${uid}/logout-everywhere`);
      toast.success(t("Signed out of all devices", "Sesión cerrada en todos los dispositivos"));
      if (self) return setUser(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="device-list">
      {!items.length && <p className="p-4 text-sm text-slate-500" data-testid="device-list-empty">{t("No devices yet", "Sin dispositivos")}</p>}
      {items.map((d) => (
        <div key={d.id} className="row-link !cursor-default" data-testid={`device-row-${d.id}`}>
          <Smartphone className="w-5 h-5 text-slate-500 shrink-0" />
          <div className="flex-1 min-w-0">
            {edit?.id === d.id ? (
              <div className="flex gap-2 items-center">
                <input className="inp !h-10" autoFocus maxLength={40} value={edit.label} placeholder={d.name} onChange={(e) => setEdit({ ...edit, label: e.target.value })} onKeyDown={(e) => e.key === "Enter" && saveLabel()} data-testid={`device-label-input-${d.id}`} />
                <button type="button" className="btn-dark btn-sm px-2" onClick={saveLabel} data-testid={`device-label-save-${d.id}`} aria-label="save"><Check className="w-4 h-4" /></button>
                <button type="button" className="btn-ghost btn-sm px-2" onClick={() => setEdit(null)} data-testid={`device-label-cancel-${d.id}`} aria-label="cancel"><X className="w-4 h-4" /></button>
              </div>
            ) : (
            <div className="font-semibold truncate" data-testid={`device-name-${d.id}`}>{d.label || d.name}
              {d.label && <span className="ml-2 text-xs font-normal text-slate-400">{d.name}</span>}
              {d.blocked && <span className="ml-2 text-xs font-bold text-red-700" data-testid={`device-blocked-badge-${d.id}`}>{t("Blocked", "Bloqueado")}</span>}
              {d.current && <span className="ml-2 text-xs font-bold text-emerald-700" data-testid="device-current-badge">{t("This device", "Este dispositivo")}</span>}
              {!d.signed_in && <span className="ml-2 text-xs text-slate-400">{t("signed out", "sesión cerrada")}</span>}
            </div>
            )}
            <div className="text-xs text-slate-500 truncate">IP {d.last_ip} · {t("last used", "último uso")} {fmtDateTime(d.last_seen, lang)} · {t("first seen", "primera vez")} {fmtDateTime(d.created_at, lang)}</div>
          </div>
          {edit?.id !== d.id && <button type="button" className="btn-ghost btn-sm px-2" onClick={() => setEdit({ id: d.id, label: d.label || "" })} data-testid={`device-rename-${d.id}`} aria-label="rename"><Pencil className="w-4 h-4" /></button>}
          <button type="button" className="btn-ghost btn-sm px-2 text-red-600" onClick={() => remove(d)} data-testid={`device-remove-${d.id}`} aria-label="remove"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <div className="p-4">
        <button type="button" className="btn-outline w-full text-red-700" onClick={everywhere} data-testid="sign-out-everywhere-btn">
          <LogOut className="w-4 h-4" /> {t("Sign out everywhere", "Cerrar sesión en todas partes")}
        </button>
      </div>
    </div>
  );
}
